<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  type: { type: String, default: 'default' },
  message: { type: String, required: true },
  title: { type: String, default: '' },
  duration: { type: Number, default: 5000 }
})

const emit = defineEmits(['close'])
const visible = ref(false)
let timeoutId = null

const iconMap = {
  success: '$CheckCircle',
  error: '$XCircle',
  warning: '$WarningCircle',
  info: '$Info',
  primary: '$Info',
  default: '$Info'
}

const currentIconClass = computed(() => iconMap[props.type] || iconMap.default)

const close = () => {
  visible.value = false
  setTimeout(() => emit('close'), 300)
}

onMounted(() => {
  requestAnimationFrame(() => {
    visible.value = true
  })

  if (props.duration > 0) {
    timeoutId = setTimeout(close, props.duration)
  }
})

onBeforeUnmount(() => {
  clearTimeout(timeoutId)
})
</script>

<template>
  <Transition name="toast-anim">
    <div
        v-if="visible"
        class="toast-notification"
        :class="[`type-${type}`]"
    >
      <div class="toast-icon-wrapper">
        <v-icon class="mdi toast-icon">{{ currentIconClass }}</v-icon>
      </div>

      <div class="toast-content">
        <p v-if="title" class="toast-title">{{ title }}</p>
        <p
          :class="['toast-message', { 'is-secondary': title }]"
          v-html="message"
        ></p>
      </div>

      <button
          @click="close"
          class="toast-close-btn"
          type="button"
      >
        <v-icon class="mdi close-icon" color="#292929">$X</v-icon>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.toast-notification {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 12px;

  width: 100%;
  min-width: 300px;
  max-width: 450px;

  padding: 16px;
  border-radius: 6px;
  background-color: #ffffff;

  font-family: "Roboto", sans-serif;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid transparent;

  box-sizing: border-box;
  transition: all 0.3s ease;
}

.type-success { border-left: 6px solid #22c55e; }
.type-success .toast-icon {
  color: #0CB935;
  background-color: #E6FBEB;
  padding: 3px;
  border-radius: 30%;
}

.type-error { border-left: 6px solid #ef4444; }
.type-error .toast-icon {
  color: #ED605E;
  background-color: #FFEBEB;
  padding: 3px;
  border-radius: 30%;
}

.type-warning { border-left: 6px solid #f59e0b; }
.type-warning .toast-icon {
  color: #f59e0b;
  background-color: #fff7eb;
  padding: 3px;
  border-radius: 30%;
}

.type-info { border-left: 6px solid #3b82f6; }
.type-info .toast-icon {
  color: #4075FF;
  background-color: #ECF1FF;
  padding: 3px;
  border-radius: 30%;
}

.type-primary { border-left: 6px solid #6366f1; }
.type-primary .toast-icon {
  color: #6366f1;
  background-color: #efefff;
  padding: 3px;
  border-radius: 30%;
}

.type-default { border-left: 6px solid #e5e7eb; }
.type-default .toast-icon {
  color: #6b7280;
  background-color: #f0f0f4;
  padding: 3px;
  border-radius: 30%;
}

.toast-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-right: 8px;
}

.toast-icon {
  font-size: 20px;
  line-height: 1;
}

.toast-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.toast-title {
  color: #1f2937;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
}

.toast-message {
  margin: 0;
  line-height: 1.4;
}

.toast-message:not(.is-secondary) {
  color: #1f2937;
  font-size: 16px;
}

.toast-message.is-secondary {
  font-size: 14px;
  color: #6b7280;
}

.toast-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 9999px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  margin-left: 4px;
}

.toast-close-btn:hover {
  background-color: #f3f4f6;
  color: #1f2937;
}

.close-icon {
  font-size: 20px;
}

.toast-anim-enter {
  opacity: 0;
  transform: translateX(100%);
}
.toast-anim-enter-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-anim-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
.toast-anim-leave-active {
  transition: all 0.3s ease-in;
}
</style>
